import { useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { ReportsOverview } from "../types";

interface DateRange {
  startDate: string;
  endDate: string;
}

interface Props {
  data?: ReportsOverview;
  isLoading: boolean;
  isExporting: boolean;
  onExportRange: (range: DateRange) => void;
  latestRange: {
    sevenDay: DateRange;
    thirtyDay: DateRange;
  };
  error?: Error | null;
}

interface SparklineSeries {
  failureMode: string;
  points: number[];
  total: number;
  latest: number;
}

const numberFormatter = new Intl.NumberFormat("en-US");

export default function ReportsView({ data, isLoading, isExporting, onExportRange, latestRange, error }: Props) {
  const top7 = data?.topFailureModes7d ?? [];
  const top30 = data?.topFailureModes30d ?? [];

  const dateSequence = useMemo(() => buildDateSequence(latestRange.thirtyDay.endDate, 30), [latestRange.thirtyDay.endDate]);

  const sparklineSeries = useMemo<SparklineSeries[]>(() => {
    const topModes = top30.slice(0, 5).map((row) => row.failureMode);
    if (!topModes.length) return [];
    const seriesMap = new Map<string, number[]>();
    topModes.forEach((mode) => {
      seriesMap.set(mode, new Array(dateSequence.length).fill(0));
    });

    for (const point of data?.trend ?? []) {
      if (!seriesMap.has(point.failureMode)) continue;
      const index = dateSequence.indexOf(point.date);
      if (index >= 0) {
        const current = seriesMap.get(point.failureMode)!;
        current[index] = point.total;
      }
    }

    return topModes.map((mode) => {
      const points = seriesMap.get(mode) ?? new Array(dateSequence.length).fill(0);
      const latest = points[points.length - 1] ?? 0;
      const total = points.reduce((acc, value) => acc + value, 0);
      return { failureMode: mode, points, latest, total };
    });
  }, [data?.trend, top30, dateSequence]);

  return (
    <div className="reports">
      <header className="reports__header">
        <div>
          <h2>Evaluation Reports</h2>
          <p className="muted">Top failure modes and trendlines derived from annotation exports.</p>
        </div>
        <div className="reports__actions">
          <button type="button" onClick={() => onExportRange(latestRange.sevenDay)} disabled={isExporting}>
            {isExporting ? "Preparing…" : "Export 7-day CSV"}
          </button>
          <button type="button" onClick={() => onExportRange(latestRange.thirtyDay)} disabled={isExporting}>
            {isExporting ? "Preparing…" : "Export 30-day CSV"}
          </button>
        </div>
      </header>

      {isLoading && !data && <div className="reports__loading">Loading reports…</div>}

      {!isLoading && error && <div className="reports__error">Failed to load reports: {error.message}</div>}

      {!isLoading && !top7.length && !top30.length && <div className="reports__empty">No annotations yet. Capture more reviews to populate reports.</div>}

      {!isLoading && (top7.length > 0 || top30.length > 0) && (
        <div className="reports__grid">
          <TopFailureTable title="Top failure modes (7 days)" rows={top7} />
          <TopFailureTable title="Top failure modes (30 days)" rows={top30} />
        </div>
      )}

      {sparklineSeries.length > 0 && (
        <section className="reports__section">
          <h3>Top failure modes — 30 day trend</h3>
          <div className="reports__sparklines">
            {sparklineSeries.map((series) => (
              <article className="reports__spark-card" key={series.failureMode}>
                <header>
                  <strong>{formatFailureModeLabel(series.failureMode)}</strong>
                  <span className="muted">{numberFormatter.format(series.total)} annotations</span>
                </header>
                <Sparkline points={series.points} />
                <footer>
                  <span>Latest day: {numberFormatter.format(series.latest)}</span>
                  <span>Range: {dateSequence[0]} → {dateSequence[dateSequence.length - 1]}</span>
                </footer>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TopFailureTable({ title, rows }: { title: string; rows: ReportsOverview["topFailureModes7d"] }) {
  return (
    <article className="reports__card">
      <header>
        <h3>{title}</h3>
      </header>
      <table>
        <thead>
          <tr>
            <th>Failure mode</th>
            <th>Total</th>
            <th>Active</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.failureMode}>
              <td>{formatFailureModeLabel(row.failureMode)}</td>
              <td>{numberFormatter.format(row.total)}</td>
              <td>{numberFormatter.format(row.activeNeedsAttention)}</td>
              <td>{row.lastSeen ? formatDistanceSafely(row.lastSeen) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </article>
  );
}

function formatDistanceSafely(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch (error) {
    console.warn("Failed to format date", iso, error);
    return iso;
  }
}

function formatFailureModeLabel(value: string): string {
  const trimmed = value?.trim();
  if (!trimmed) return "unspecified";
  if (trimmed.toLowerCase() === "unspecified") return "unspecified";
  return trimmed;
}

function Sparkline({ points }: { points: number[] }) {
  const width = 160;
  const height = 40;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - Math.min(min, 0);
  const stepX = points.length > 1 ? width / (points.length - 1) : width;
  const path = points
    .map((value, index) => {
      const x = points.length > 1 ? index * stepX : width / 2;
      const normalized = range === 0 ? 0 : (value - Math.min(min, 0)) / (range || 1);
      const y = height - normalized * (height - 6) - 3;
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg className="reports__sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-hidden="true">
      <path d={path} fill="none" stroke="rgba(59, 130, 246, 0.85)" strokeWidth="2" />
    </svg>
  );
}

function buildDateSequence(endDateIso: string, days: number): string[] {
  const end = new Date(`${endDateIso}T00:00:00Z`);
  const result: string[] = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const current = new Date(end);
    current.setUTCDate(current.getUTCDate() - offset);
    result.push(current.toISOString().slice(0, 10));
  }
  return result;
}
